// Must mirror drain-core's AC12 phantom-guard normalisation (Story 7.4) so the interactive save
// path and the background drain apply the same equality truth over Postgres-JSONB round-trips — a
// self-reconcile 409 that drain-core would treat as a phantom must read identically here.
// Standalone (not imported from `drain-core`) so the save path reuses it without pulling the
// drain module.

/** Normalize a live JS value the way Postgres JSONB already normalized a stored value. */
function jsonNormalize(value: unknown): unknown {
  return JSON.parse(JSON.stringify(value ?? null));
}

/** Order-insensitive (object keys) / positional (arrays) deep equality over two normalized values. */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (typeof a === "object") {
    const ao = a as Record<string, unknown>;
    const bo = b as Record<string, unknown>;
    const ak = Object.keys(ao);
    const bk = Object.keys(bo);
    if (ak.length !== bk.length) return false;
    return ak.every(
      (k) => Object.prototype.hasOwnProperty.call(bo, k) && deepEqual(ao[k], bo[k]),
    );
  }
  return false;
}

/** True when `a` and `b` are deeply equal after JSONB-style normalization. */
export function deepJsonEqual(a: unknown, b: unknown): boolean {
  try {
    return deepEqual(jsonNormalize(a), jsonNormalize(b));
  } catch {
    // `JSON.stringify` throws on a cycle / non-serialisable value. The signature accepts `unknown`,
    // so keep the EVAL-002 catch path safe for any future caller: treat unserialisable as diverged.
    return false;
  }
}
